import { useRef, useEffect, useMemo, useState, useCallback } from "react";
import type { SubtitleLine, FuriganaSegment } from "@/types/subtitle";
import { cn } from "@/lib/utils";
import { BookPlus, X, Volume2, Loader2, Check, Settings, EyeOff, Eye, Mic, Heart, Info, Bot, Home, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { VocabularyWord } from "@/hooks/useVocabulary";
import { useKuromoji, KuromojiToken } from "@/hooks/useKuromoji";
import { posColorMap } from "./SubtitleTextComponents"; // Import posColorMap

import { WordPopup, lookupWord, WordInfo } from "./WordPopup";
import { SubtitleItem } from "./SubtitleItem"; // Imported the optimized component

interface SubtitlePanelProps {
  subtitles: SubtitleLine[];
  subtitlesLoading?: boolean;
  currentTime: number;
  onSeek: (time: number) => void;
  showFurigana?: boolean;
  showRomaji?: boolean;
  showChinese?: boolean;
  videoId?: string;
  hideUnderline?: boolean;
  hideGrammarBackground?: boolean;
  hideShadowingSpotlight?: boolean;
  shadowingMode?: boolean;
  showOnlyChinese?: boolean;
  highlightNewWords?: boolean;
  vocabulary?: VocabularyWord[];
  // Settings setters
  setShowFurigana?: (v: boolean) => void;
  setShowRomaji?: (v: boolean) => void;
  setShowChinese?: (v: boolean) => void;
  setHideUnderline?: (v: boolean) => void;
  setHideGrammarBackground?: (v: boolean) => void;
  setHideShadowingSpotlight?: (v: boolean) => void;
  setShadowingMode?: (v: boolean) => void;
  setShowOnlyChinese?: (v: boolean) => void;
  setHighlightNewWords?: (v: boolean) => void;
  theme?: string;
  setTheme?: (v: string) => void;
  onOpenSettings?: () => void;
  isLiked?: boolean;
  onToggleLike?: () => void;
  tokenizer?: any; // Add tokenizer prop to interface
  tokenizerError?: Error | null;
  // New props for font size and blur mask
  fontSize?: number;
  blurStrength?: number;
  setFontSize?: (v: number) => void;
  setBlurStrength?: (v: number) => void;
  onAnalyzeSentence?: () => void;
  onGoHome?: () => void;
}

type SubtitleExportMode = "bilingual" | "japanese" | "chinese";

export function SubtitlePanel({
  subtitles,
  subtitlesLoading = false,
  currentTime,
  onSeek,
  showFurigana = true,
  showRomaji = false,
  showChinese = true,
  videoId,
  hideUnderline = false,
  hideGrammarBackground = false,
  hideShadowingSpotlight = false,
  shadowingMode = false,
  showOnlyChinese = false,
  highlightNewWords = true,
  vocabulary = [],
  tokenizer, // Ensure tokenizer is destructured here!
  tokenizerError,
  setShowFurigana = () => {},
  setShowRomaji = () => {},
  setShowChinese = () => {},
  setHideUnderline = () => {},
  setHideGrammarBackground = () => {},
  setHideShadowingSpotlight = () => {},
  setShadowingMode = () => {},
  setShowOnlyChinese = () => {},
  setHighlightNewWords = () => {},
  theme = "system",
  setTheme = () => {},
  onOpenSettings,
  isLiked,
  onToggleLike,
  fontSize = 1,
  blurStrength = 0,
  setFontSize = () => {},
  setBlurStrength = () => {},
  onAnalyzeSentence,
  onGoHome,
}: SubtitlePanelProps) {
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);
  const tapStartRef = useRef<{
    pointerId: number;
    x: number;
    y: number;
    t: number;
    startTime: number;
    moved: boolean;
  } | null>(null);
  const lastSeekRef = useRef<{ at: number; time: number } | null>(null);
  const suppressAutoScrollUntilRef = useRef(0);
  const suppressSeekUntilRef = useRef(0);
  const lastScrollGestureAtRef = useRef(0);
  const [popupWord, setPopupWord] = useState<string | null>(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [hoverWord, setHoverWord] = useState<string | null>(null);
  const [hoverPos, setHoverPos] = useState<string>("未知");
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const hoverTimerRef = useRef<number | null>(null);
  const [savedSentences, setSavedSentences] = useState<Set<string>>(new Set());
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  // REMOVE local hook call to avoid shadowing the prop!
  // const { tokenizer } = useKuromoji(); 
  const [isTokenizerEnabled, setIsTokenizerEnabled] = useState(false);
  // Furigana fallback and POS tagging are intentionally decoupled:
  // - showFurigana: allows tokenizer for reading fallback
  // - isTokenizerEnabled: controls POS coloring behavior
  const effectiveTokenizer = (showFurigana || isTokenizerEnabled) ? tokenizer : undefined;

  // Vocabulary set for efficient lookup
  const vocabularySet = useMemo(() => {
    return new Set(vocabulary.map(v => v.word));
  }, [vocabulary]);

  const findSubtitleAtTime = useCallback(
    (time: number) => {
      let left = 0;
      let right = subtitles.length - 1;

      while (left <= right) {
        const mid = (left + right) >> 1;
        const sub = subtitles[mid];

        if (time < sub.startTime) {
          right = mid - 1;
        } else if (time >= sub.endTime) {
          left = mid + 1;
        } else {
          return sub;
        }
      }

      return undefined;
    },
    [subtitles],
  );

  // Find current subtitle
  const currentSubtitle = useMemo(
    () => findSubtitleAtTime(currentTime),
    [findSubtitleAtTime, currentTime],
  );

  // Auto-scroll to active subtitle
  useEffect(() => {
    const now = Date.now();
    
    // Safety check: if tap interaction is stuck (e.g. missed pointerup), clear it after 3s
    if (tapStartRef.current && now - tapStartRef.current.t > 3000) {
      tapStartRef.current = null;
    }

    if (tapStartRef.current) return;
    if (now < suppressAutoScrollUntilRef.current) return;

    if (activeRef.current && containerRef.current) {
      const container = containerRef.current;
      const active = activeRef.current;
      const containerRect = container.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      
      const activeCenterY = activeRect.top + activeRect.height / 2;
      const followBandTop = containerRect.top + containerRect.height * 0.2;
      const followBandBottom = containerRect.bottom - containerRect.height * 0.2;
      const shouldScroll = activeCenterY < followBandTop || activeCenterY > followBandBottom;

      if (shouldScroll) {
        active.scrollIntoView({ behavior: isMobile ? "auto" : "smooth", block: "center" });
      }
    }
  }, [currentSubtitle?.id, isMobile]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPopupWord(null);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const escapeHtml = (input: string) =>
    input
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const handlePrintSubtitlesPdf = useCallback((mode: SubtitleExportMode) => {
    if (!subtitles.length) {
      toast({ title: "暂无字幕可导出", variant: "destructive" });
      return;
    }

    setPrintDialogOpen(false);

    // Keep popup options simple for Safari/mac compatibility.
    const popup = window.open("", "_blank", "width=980,height=760");
    if (!popup) {
      toast({
        title: "无法打开打印窗口",
        description: "请关闭浏览器弹窗拦截后重试",
        variant: "destructive",
      });
      return;
    }

    const modeLabelMap: Record<SubtitleExportMode, string> = {
      bilingual: "双语",
      japanese: "日文",
      chinese: "中文",
    };
    const modeLabel = modeLabelMap[mode];
    const columns =
      mode === "bilingual"
        ? ([
            { key: "japanese", label: "日文" },
            { key: "chinese", label: "中文" },
          ] as const)
        : mode === "japanese"
          ? ([{ key: "japanese", label: "日文" }] as const)
          : ([{ key: "chinese", label: "中文" }] as const);

    const title = document.title || "字幕导出";
    const generatedAt = new Date().toLocaleString("zh-CN", { hour12: false });
    const logoUrl = `${window.location.origin}/favicon.ico`;

    const headerCells = columns.map((column) => `<th>${column.label}</th>`).join("");
    const rows = subtitles
      .map((item, idx) => {
        const contentCells = columns
          .map((column) => {
            const value = column.key === "japanese" ? item.japanese || "" : item.chinese || "";
            return `<td>${escapeHtml(value)}</td>`;
          })
          .join("");

        return `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(formatTime(item.startTime))}</td>
            ${contentCells}
          </tr>
        `;
      })
      .join("");

    popup.document.open();
    popup.document.write(`
      <!doctype html>
      <html lang="zh-CN">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <title>${escapeHtml(title)} - 字幕打印</title>
        <style>
          :root { color-scheme: light; }
          body { margin: 22px; font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Noto Sans CJK SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif; color: #111827; }
          .header { text-align: center; margin-bottom: 16px; }
          .header h1 { margin: 0; font-size: 22px; letter-spacing: 0.4px; }
          .logo-wrap { margin-top: 8px; display: flex; justify-content: center; }
          .logo-wrap img { width: 44px; height: 44px; border-radius: 10px; object-fit: contain; }
          .site { margin-top: 6px; font-size: 12px; color: #6b7280; }
          .meta { margin-bottom: 12px; color: #6b7280; font-size: 12px; text-align: center; }
          table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          th, td { border: 1px solid #e5e7eb; padding: 7px 8px; font-size: 12px; vertical-align: top; line-height: 1.45; word-break: break-word; }
          th { background: #f8fafc; text-align: left; }
          th:nth-child(1), td:nth-child(1) { width: 44px; text-align: center; }
          th:nth-child(2), td:nth-child(2) { width: 70px; text-align: center; white-space: nowrap; }
          @media print {
            body { margin: 12mm; }
            @page { size: A4 portrait; margin: 12mm; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${escapeHtml(title)}</h1>
          <div class="logo-wrap">
            <img src="${escapeHtml(logoUrl)}" alt="DangoJP Logo" onerror="this.style.display='none'" />
          </div>
          <div class="site">https://dangojp.top</div>
        </div>
        <div class="meta">导出模式：${escapeHtml(modeLabel)} ｜ 导出时间：${escapeHtml(generatedAt)} ｜ 字幕条数：${subtitles.length}</div>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>时间</th>
              ${headerCells}
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </body>
      </html>
    `);
    popup.document.close();

    const triggerPrint = () => {
      try {
        popup.focus();
        popup.print();
      } catch {
        // ignore
      }
    };

    if (popup.document.readyState === "complete") {
      window.setTimeout(triggerPrint, 120);
    } else {
      popup.onload = () => window.setTimeout(triggerPrint, 120);
    }

    toast({
      title: "已打开打印窗口",
      description: "在打印面板选择“存储为 PDF”即可下载",
    });
  }, [formatTime, subtitles, toast]);

  const handleWordClick = (word: string, rect: DOMRect) => {
    // Prevent the parent card from treating this gesture as a tap-to-seek.
    // Make sure we suppress long enough to prevent any scroll jitter
    suppressSeekUntilRef.current = Date.now() + 800;
    
    // CRITICAL FIX: Do NOT suppress auto-scroll here.
    // We want the video to keep playing and subtitles to keep scrolling 
    // even while the popup is open.
    // suppressAutoScrollUntilRef.current = Date.now() + 1000;
    
    setPopupWord(word);
    setPopupPosition({ x: rect.left, y: rect.bottom });
  };

  const handleWordHover = (word: string, rect: DOMRect) => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHoverWord(word);
    setHoverPosition({ x: rect.left, y: rect.top });
    if (isTokenizerEnabled && tokenizer) {
      try {
        const token = tokenizer.tokenize(word)?.[0];
        setHoverPos(token?.pos || "未知");
      } catch {
        setHoverPos("未知");
      }
    } else {
      setHoverPos("未知");
    }
  };

  const isSeekBlockedTarget = useCallback((target: EventTarget | null) => {
    if (!(target instanceof Element)) return false;
    // Block seek on buttons, links, and words (data-dict) to allow lookup without seeking
    return !!target.closest("button,a,[role='button'],[data-no-seek='1'],[data-dict='1']");
  }, []);

  const seekOnce = useCallback(
    (time: number) => {
      lastSeekRef.current = { at: time, time: Date.now() };
      onSeek(time);
    },
    [onSeek]
  );

  const handleAddToVocabulary = async (info: WordInfo) => {
    if (!user) {
      toast({ title: "请先登录", variant: "destructive" });
      setPopupWord(null);
      return;
    }

    try {
      const { error } = await supabase.from("vocabulary").insert({
        user_id: user.id,
        word: info.word,
        reading: info.reading,
        romaji: info.romaji,
        meaning: info.meaning,
        chinese_translation: info.chineseTranslation || null,
        part_of_speech: info.partOfSpeech,
        example_sentence: info.example || null,
        source_video_id: videoId || null,
      });

      if (error) throw error;
      toast({ title: "已加入生词本" });
      setPopupWord(null);
    } catch (error) {
      console.error("Failed to add vocabulary:", error);
      toast({ title: "保存失败", variant: "destructive" });
    }
  };

  const handleSaveSentence = async (subtitle: SubtitleLine) => {
    if (!user) {
      toast({ title: "请先登录", variant: "destructive" });
      return;
    }

    if (savedSentences.has(subtitle.id)) {
      toast({ title: "已收藏过了" });
      return;
    }

    try {
      const { error } = await supabase.from("vocabulary").insert({
        user_id: user.id,
        word: subtitle.japanese,
        meaning: subtitle.chinese,
        romaji: subtitle.romaji || null,
        source_video_id: videoId || null,
      });

      if (error) throw error;
      setSavedSentences(prev => new Set(prev).add(subtitle.id));
      toast({ title: "句子已收藏" });
    } catch (error) {
      console.error("Failed to save sentence:", error);
      toast({ title: "保存失败", variant: "destructive" });
    }
  };

  // Simple tap-to-seek: any tap on the card triggers seek (unless on a button)
  const handleSubtitleClick = (e: React.MouseEvent, startTime: number) => {
    // Desktop-only (mobile uses pointer gesture logic for reliability)
    if (isMobile) return;

    // Debounce
    const last = lastSeekRef.current;
    if (last && last.at === startTime && Date.now() - last.time < 500) return;

    if (isSeekBlockedTarget(e.target)) return;

    // Don't jump if user is selecting text
    const selection = window.getSelection();
    if (selection?.toString().trim()) {
      return;
    }
    seekOnce(startTime);
  };

  const TAP_MOVE_PX = 10;
  const TAP_MAX_MS = 450;

  const handleSubtitlePointerDown = (e: React.PointerEvent, startTime: number) => {
    // On mobile we also accept "mouse" pointer events (some browsers/automation report mouse).
    if (!isMobile && e.pointerType === "mouse") return;
    if (isSeekBlockedTarget(e.target)) return;

    // Remove setPointerCapture to allow native scrolling on iOS
    // We rely on move distance to detect scroll vs tap

    suppressAutoScrollUntilRef.current = Date.now() + 1200;
    tapStartRef.current = {
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      t: Date.now(),
      startTime,
      moved: false,
    };
  };

  const handleSubtitlePointerMove = (e: React.PointerEvent) => {
    const start = tapStartRef.current;
    if (!start || start.pointerId !== e.pointerId) return;

    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    // Increased threshold slightly for better scroll detection
    if (Math.hypot(dx, dy) > TAP_MOVE_PX) {
      start.moved = true;
      suppressAutoScrollUntilRef.current = Date.now() + 2000;
    }
  };

  const handleSubtitlePointerUp = (e: React.PointerEvent) => {
    const start = tapStartRef.current;
    if (!start || start.pointerId !== e.pointerId) return;
    tapStartRef.current = null;

    if (start.moved) {
      lastScrollGestureAtRef.current = Date.now();
      return;
    }

    if (Date.now() - start.t > TAP_MAX_MS) return;
    if (Date.now() < suppressSeekUntilRef.current) return;

    // Don't jump if user is selecting text
    const selection = window.getSelection();
    if (selection?.toString().trim()) return;

    seekOnce(start.startTime);
  };

  const handleSubtitlePointerCancel = (e: React.PointerEvent) => {
    const start = tapStartRef.current;
    if (!start || start.pointerId !== e.pointerId) return;
    tapStartRef.current = null;
    // Browser took over (e.g. scroll), so it's not a tap
  };

  return (
    <div className="relative isolate flex flex-col h-full bg-card rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 z-20 flex items-center justify-between px-1.5 py-1 sm:px-3 sm:py-2 border-b border-border bg-muted/90 backdrop-blur-sm">
        <div className="flex items-center gap-2 overflow-hidden">
          <h3 className="font-bold text-sm sm:text-base text-foreground shrink-0">字幕</h3>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 sm:h-7 sm:w-7 shrink-0"
            onClick={onGoHome}
            title="首页"
          >
            <Home className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
          </Button>
          {/* Debug indicator for Tokenizer */}
          <div className="flex flex-col items-start sm:items-end">
            <div className="flex items-center gap-1 sm:gap-2">
              {tokenizer && (
                <div className="flex items-center gap-1 sm:gap-2 mr-1 sm:mr-2 max-[390px]:hidden">
                  <span className="text-[10px] sm:text-xs text-muted-foreground whitespace-nowrap max-[390px]:hidden">分词</span>
                  <Switch
                    checked={isTokenizerEnabled}
                    onCheckedChange={setIsTokenizerEnabled}
                    className="scale-75 max-[390px]:scale-[0.65] origin-left sm:origin-center"
                  />
                </div>
              )}
              
                <Popover>
                  <PopoverTrigger asChild>
                  <Button type="button" variant="ghost" size="icon" className="h-5 w-5 sm:h-6 sm:w-6 rounded-full hover:bg-muted p-0 shrink-0">
                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="sr-only">词性图例</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3" align="end">
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm border-b pb-2 mb-2">词性颜色图例</h4>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      {Object.entries(posColorMap).map(([pos, className]) => (
                        <div key={pos} className="flex items-center gap-2">
                          <span className={cn("font-medium", className)}>
                            {pos}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-5 w-5 sm:h-6 sm:w-6 rounded-full hover:bg-muted p-0 shrink-0"
                onClick={() => setPrintDialogOpen(true)}
                title="打印字幕并下载 PDF"
              >
                <Printer className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="sr-only">打印字幕并下载 PDF</span>
              </Button>

              {!tokenizer && !tokenizerError && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-yellow-100 text-yellow-700 border-yellow-200 whitespace-nowrap">
                  Loading...
                </span>
              )}
            </div>
            {tokenizerError && (
              <span className="text-[10px] text-red-500 mt-1 max-w-[150px] truncate" title={String(tokenizerError)}>
                {String(tokenizerError)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-0.5 sm:gap-2 text-xs shrink-0">
          <span className="text-muted-foreground text-[10px] sm:text-xs mr-1 hidden sm:inline">
            {subtitles.length} 句
          </span>
          <span className="text-muted-foreground text-[10px] mr-1 sm:hidden max-[430px]:hidden">
            {subtitles.length}
          </span>
          {setShadowingMode && (
            <Button 
              type="button"
              variant="ghost" 
              size="icon" 
              className={cn(
                "h-7 w-7 sm:h-8 sm:w-8",
                shadowingMode && "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800"
              )}
              onClick={() => setShadowingMode?.(!shadowingMode)}
              title="影子跟读模式"
            >
              <Mic className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn("h-7 w-7 sm:h-8 sm:w-8", isLiked && "text-rose-500")}
            onClick={onToggleLike}
            title={isLiked ? "取消收藏" : "收藏"}
          >
            <Heart className={cn("h-3.5 w-3.5 sm:h-4 sm:w-4", isLiked && "fill-current")} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 sm:h-8 sm:w-8"
            onClick={onAnalyzeSentence}
            title="AI句子解析"
          >
            <Bot className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-500" />
          </Button>
          <Button 
            type="button"
            variant="ghost" 
            size="icon" 
            className="h-7 w-7 sm:h-8 sm:w-8"
            onClick={onOpenSettings}
          >
            <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
          </Button>
        </div>
      </div>

      {/* Subtitle list */}
      <div
        ref={containerRef}
        data-live-scroll="subtitle-list"
        className="relative z-0 flex-1 overflow-y-auto p-1.5 max-[390px]:p-1 sm:p-3 space-y-1 max-[390px]:space-y-0.5 sm:space-y-2 scrollbar-zen will-change-scroll"
        onScroll={() => {
          // If the user is actively touching/dragging, pause auto-follow.
          // IMPORTANT: do NOT mark the gesture as "moved" here—scroll may be caused by auto-follow.
          if (tapStartRef.current) {
            suppressAutoScrollUntilRef.current = Date.now() + 2000;
          }
        }}
      >
        {subtitlesLoading ? (
          <div className="h-full min-h-[220px] flex flex-col items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mb-2" />
            <p className="text-sm">字幕加载中...</p>
          </div>
        ) : subtitles.length === 0 ? (
          <div className="h-full min-h-[220px] flex flex-col items-center justify-center text-center text-muted-foreground px-4">
            <p className="text-sm">未读取到字幕</p>
            <p className="text-xs mt-1">该内容可能还未上传字幕，或字幕仍在处理中。</p>
          </div>
        ) : (
          subtitles.map((subtitle) => {
            const isActive = currentSubtitle?.id === subtitle.id;
            const isSaved = savedSentences.has(subtitle.id);
            
            return (
              <div
                key={subtitle.id}
                ref={isActive ? activeRef : null}
                className="block"
              >
              <SubtitleItem
                subtitle={subtitle}
                isActive={isActive}
                isSaved={isSaved}
                currentTime={currentTime}
                isMobile={isMobile}
                showFurigana={showFurigana}
                showChinese={showChinese}
                showRomaji={showRomaji}
                showOnlyChinese={showOnlyChinese}
                hideUnderline={hideUnderline}
                hideGrammarBackground={hideGrammarBackground}
                hideShadowingSpotlight={hideShadowingSpotlight}
                highlightNewWords={highlightNewWords}
                shadowingMode={shadowingMode}
                vocabularySet={vocabularySet}
                tokenizer={effectiveTokenizer}
                enablePosTagging={isTokenizerEnabled}
                fontSize={fontSize}
                blurStrength={blurStrength}
                onSeek={seekOnce}
                onSaveSentence={handleSaveSentence}
                onWordClick={handleWordClick}
                onWordHover={handleWordHover}
                onPointerDown={handleSubtitlePointerDown}
                onPointerMove={handleSubtitlePointerMove}
                onPointerUp={handleSubtitlePointerUp}
                onPointerCancel={handleSubtitlePointerCancel}
                onMouseLeave={() => {
                  setHoverWord(null);
                  if (hoverTimerRef.current) {
                    clearTimeout(hoverTimerRef.current);
                    hoverTimerRef.current = null;
                  }
                }}
              />
              </div>
            );
          })
        )}
      </div>

      {/* Word Popup */}
      {popupWord && (
        <WordPopup
          word={popupWord}
          position={popupPosition}
          onClose={() => setPopupWord(null)}
          onAddToVocabulary={handleAddToVocabulary}
        />
      )}
      {hoverWord && (
        <div
          className="fixed z-40 bg-background/70 backdrop-blur-sm border border-border rounded-md px-2 py-1 text-xs text-muted-foreground"
          style={{ left: Math.min(hoverPosition.x, window.innerWidth - 200), top: hoverPosition.y - 28 }}
        >
          词性：{hoverPos}
        </div>
      )}

      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="max-w-xs sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>选择字幕打印模式</DialogTitle>
            <DialogDescription>
              请选择导出内容：双语、仅日文、仅中文。
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={() => handlePrintSubtitlesPdf("bilingual")}>
              双语（日 + 中）
            </Button>
            <Button type="button" variant="secondary" onClick={() => handlePrintSubtitlesPdf("japanese")}>
              仅日文
            </Button>
            <Button type="button" variant="secondary" onClick={() => handlePrintSubtitlesPdf("chinese")}>
              仅中文
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
